import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/loader";
import DOCUMENTS from "../../api/services/DOCUMENTS";

// ─── Constants ──────────────────────────────────────────────────────────────────

const TAX_STATUS_OPTIONS = [
  {
    value: "BIG",
    title: "עוסק מורשה",
    code: "BIG",
    desc: "מחזור שנתי מעל הסף הפטור — נגבה מע״מ 18% על כל הפקה.",
  },
  {
    value: "COMPANY",
    title: "חברה בע״מ",
    code: "COMPANY",
    desc: "חברה רשומה אצל רשם החברות — נגבה מע״מ 18% על כל הפקה.",
  },
  {
    value: "PATUR",
    title: "עוסק פטור",
    code: "PATUR",
    desc: "מחזור שנתי מתחת לסף — אינך גובה מע״מ מלקוחותיך.",
  },
  {
    value: "FREELANCER",
    title: "עוסק זעיר",
    code: "FREELANCER",
    desc: "מסלול עוסק זעיר — ללא גביית מע״מ.",
  },
];

// ─── Styles ─────────────────────────────────────────────────────────────────────

const S = {
  page: {
    padding: 24,
    direction: "rtl",
    fontFamily: "Heebo, Arial, sans-serif",
    maxWidth: 900,
    margin: "0 auto",
  },
  header: { marginBottom: 28 },
  title: { fontSize: 28, fontWeight: 800, color: "#111827", margin: 0 },
  subtitle: { fontSize: 14, color: "#6B7280", marginTop: 6 },

  section: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 16,
    padding: 24,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    margin: 0,
    marginBottom: 6,
  },
  sectionHint: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 18,
    lineHeight: 1.6,
  },

  // Radio cards (tax status)
  radioGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
  },
  radioCard: (selected) => ({
    border: selected ? "2px solid #E93D67" : "1px solid #E5E7EB",
    background: selected ? "#FFF1F4" : "#fff",
    borderRadius: 12,
    padding: 14,
    cursor: "pointer",
    transition: "all 120ms ease",
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  }),
  radioBullet: (selected) => ({
    width: 20,
    height: 20,
    borderRadius: 10,
    border: selected ? "6px solid #E93D67" : "2px solid #D1D5DB",
    background: "#fff",
    flexShrink: 0,
    marginTop: 2,
  }),
  radioTitle: { fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 4 },
  radioCode: {
    fontSize: 11,
    fontWeight: 600,
    color: "#9CA3AF",
    background: "#F3F4F6",
    borderRadius: 4,
    padding: "1px 6px",
    marginInlineStart: 6,
  },
  radioDesc: { fontSize: 13, color: "#6B7280", lineHeight: 1.5 },

  noteBox: {
    marginTop: 14,
    padding: "10px 14px",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    fontSize: 13,
    color: "#374151",
    lineHeight: 1.5,
  },

  // VAT input
  input: {
    padding: "10px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    fontSize: 15,
    width: "100%",
    maxWidth: 320,
    fontFamily: "inherit",
    textAlign: "right",
    boxSizing: "border-box",
  },

  // Sequence rows
  seqRow: {
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    background: "#fff",
  },
  seqRowLocked: { background: "#F9FAFB" },
  seqRowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  seqLabel: { fontSize: 15, fontWeight: 700, color: "#111827" },
  seqLockHint: {
    fontSize: 12,
    color: "#6B7280",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  seqStatRow: {
    display: "flex",
    gap: 16,
    fontSize: 13,
    color: "#374151",
    marginTop: 4,
  },
  seqStat: { display: "flex", alignItems: "center", gap: 6 },
  seqStatNum: {
    fontWeight: 700,
    color: "#111827",
    background: "#F3F4F6",
    padding: "2px 10px",
    borderRadius: 6,
    fontVariantNumeric: "tabular-nums",
  },
  seqEditRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    flexWrap: "wrap",
  },
  seqInput: {
    padding: "8px 12px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    fontSize: 14,
    width: 120,
    fontFamily: "inherit",
    textAlign: "right",
  },
  seqHint: { fontSize: 12, color: "#6B7280" },

  // Save button
  saveRow: {
    position: "sticky",
    bottom: 0,
    background: "#fff",
    borderTop: "1px solid #E5E7EB",
    padding: "16px 0",
    marginTop: 8,
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  },
  saveBtn: (disabled) => ({
    background: disabled ? "#F3A9BC" : "#E93D67",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 24px",
    fontSize: 15,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    gap: 8,
  }),
};

// ─── Component ──────────────────────────────────────────────────────────────────

const DocumentsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // server snapshot — used to detect what actually changed so we don't try to
  // re-write a locked sequence's startFrom on every save.
  const [serverSetup, setServerSetup] = useState(null);

  // editable form state
  const [taxStatus, setTaxStatus] = useState("BIG");
  const [vat, setVat] = useState("");
  const [seqDrafts, setSeqDrafts] = useState({}); // { docType: string }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DOCUMENTS.get_setup();
      setServerSetup(res);
      setTaxStatus(res.taxStatus || "BIG");
      setVat(res.vat || "");
      const initial = {};
      (res.sequences || []).forEach((s) => {
        initial[s.docType] = String(s.startFrom ?? 1);
      });
      setSeqDrafts(initial);
    } catch (e) {
      toast.error("טעינת ההגדרות נכשלה: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!serverSetup) return;

    // Build the payload: only include taxStatus/vat if they changed, and only
    // include startFrom entries for unlocked sequences whose value actually
    // changed (locked sequences will 409 the whole request).
    const payload = {};
    if (taxStatus && taxStatus !== serverSetup.taxStatus) {
      payload.taxStatus = taxStatus;
    }
    const trimmedVat = (vat || "").trim();
    if (trimmedVat && trimmedVat !== (serverSetup.vat || "")) {
      if (trimmedVat.length < 5 || trimmedVat.length > 20) {
        toast.error("מספר עוסק / ח.פ. חייב להיות בין 5 ל-20 תווים");
        return;
      }
      payload.vat = trimmedVat;
    }

    const startFrom = {};
    for (const s of serverSetup.sequences || []) {
      if (s.locked) continue;
      const draft = Number(seqDrafts[s.docType]);
      if (!Number.isInteger(draft) || draft < 1) {
        toast.error(`מספר התחלה לסוג ${s.docType} חייב להיות שלם וגדול מ-0`);
        return;
      }
      if (draft !== s.startFrom) {
        startFrom[String(s.docType)] = draft;
      }
    }
    if (Object.keys(startFrom).length) {
      payload.startFrom = startFrom;
    }

    if (Object.keys(payload).length === 0) {
      toast.info("לא בוצעו שינויים");
      return;
    }

    setSaving(true);
    try {
      await DOCUMENTS.post_setup(payload);
      toast.success("ההגדרות נשמרו");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "שמירת ההגדרות נכשלה";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !serverSetup) {
    return (
      <div style={S.page}>
        <Loader />
      </div>
    );
  }

  const vatLiable = taxStatus === "BIG" || taxStatus === "COMPANY";

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>הגדרות חשבוניות</h1>
        <div style={S.subtitle}>
          הגדר את סוג העוסק, מספר העוסק והמספור של כל סוג מסמך. הגדרות אלו מוטמעות בכל מסמך שיופק.
        </div>
      </div>

      {/* ── Section 1: tax status ───────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>סוג עוסק</h2>
        <div style={S.sectionHint}>
          סוג העוסק קובע אם נגבה מע״מ בכל מסמך שמופק. ניתן לשנות בכל עת.
        </div>
        <div style={S.radioGrid}>
          {TAX_STATUS_OPTIONS.map((opt) => {
            const selected = taxStatus === opt.value;
            return (
              <div
                key={opt.value}
                style={S.radioCard(selected)}
                onClick={() => setTaxStatus(opt.value)}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setTaxStatus(opt.value);
                  }
                }}
              >
                <div style={S.radioBullet(selected)} />
                <div style={{ flex: 1 }}>
                  <div style={S.radioTitle}>
                    {opt.title}
                    <span style={S.radioCode}>{opt.code}</span>
                  </div>
                  <div style={S.radioDesc}>{opt.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={S.noteBox}>
          {vatLiable
            ? "המסמכים שתפיק יכללו שורת מע״מ 18% לפי הוראות רשות המסים."
            : "המסמכים שתפיק לא יכללו מע״מ — סכום ביניים שווה לסה״כ לתשלום."}
        </div>
      </div>

      {/* ── Section 2: VAT number ───────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>מספר עוסק / ח.פ.</h2>
        <div style={S.sectionHint}>
          המספר שמופיע בכותרת כל מסמך שמופק. דרישה של רשות המסים.
        </div>
        <input
          type="text"
          inputMode="numeric"
          style={S.input}
          value={vat}
          onChange={(e) => setVat(e.target.value)}
          placeholder="לדוגמה: 123456789"
        />
      </div>

      {/* ── Section 3: numbering ────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>מספור מסמכים</h2>
        <div style={S.sectionHint}>
          ניתן לקבוע מספר התחלה לכל סוג מסמך. לאחר הפקת המסמך הראשון מסוג כלשהו, המספור עבורו ננעל ולא ניתן לשינוי — דרישה של רשות המסים.
        </div>
        {(serverSetup.sequences || []).map((s) => {
          const locked = !!s.locked;
          return (
            <div
              key={s.docType}
              style={{ ...S.seqRow, ...(locked ? S.seqRowLocked : null) }}
            >
              <div style={S.seqRowHeader}>
                <div style={S.seqLabel}>
                  {s.label}{" "}
                  <span style={{ color: "#9CA3AF", fontWeight: 500, fontSize: 13 }}>
                    ({s.docType})
                  </span>
                </div>
                {locked && (
                  <div style={S.seqLockHint}>
                    <span>🔒</span>
                    <span>נעול — {s.lastIssued} מסמכים הופקו</span>
                  </div>
                )}
              </div>
              {locked ? (
                <div style={S.seqStatRow}>
                  <div style={S.seqStat}>
                    <span>מספר אחרון:</span>
                    <span style={S.seqStatNum}>{s.lastIssued}</span>
                  </div>
                  <div style={S.seqStat}>
                    <span>הבא:</span>
                    <span style={S.seqStatNum}>{s.lastIssued + 1}</span>
                  </div>
                </div>
              ) : (
                <div style={S.seqEditRow}>
                  <span style={S.seqHint}>מספר התחלה:</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    style={S.seqInput}
                    value={seqDrafts[s.docType] ?? ""}
                    onChange={(e) =>
                      setSeqDrafts({ ...seqDrafts, [s.docType]: e.target.value })
                    }
                  />
                  <span style={S.seqHint}>טרם הופקו מסמכים מסוג זה</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Save bar ───────────────────────────────────────────────── */}
      <div style={S.saveRow}>
        <button
          style={S.saveBtn(saving)}
          onClick={handleSave}
          disabled={saving}
        >
          <span style={{ fontSize: 18 }}>💾</span>
          {saving ? "שומר..." : "שמור הגדרות"}
        </button>
      </div>
    </div>
  );
};

export default DocumentsSettings;
